/*
 * Onyu announcement bar motion — <onyu-ticker> (snippets/onyu-announcement-ticker.liquid).
 *
 * data-mode="fade"   One announcement at a time. Every few seconds the current one fades
 *                    upward while the next rises from below into its place.
 * data-mode="ticker" All announcements in one continuously scrolling line. The rendered
 *                    group is repeated until it is wider than the bar, that whole set is
 *                    cloned once, and the track is animated by exactly one set's width —
 *                    so the line loops with no seam and no gap on wide screens. Duration
 *                    comes from a pixels-per-second speed, so long and short announcements
 *                    travel at the same pace.
 *
 * Both modes pause while the pointer is over the bar or something inside it has keyboard
 * focus, so a discount code can be read and copied. With reduced motion nothing slides:
 * fade mode cross-fades in place, ticker mode stays still.
 */
if (!customElements.get('onyu-ticker')) {
  class OnyuTicker extends HTMLElement {
    constructor() {
      super();
      this.minDuration = 8; // ticker: seconds — keeps very short bars from whipping past
      this.fadeDuration = 600; // ms — must cover --onyu-fade-duration in the CSS
      this.index = 0;
      this.paused = false;
      this.onResize = this.debounce(() => this.layout(), 200);
      this.onMotionChange = () => this.layout();
      this.onEnter = () => {
        this.paused = true;
      };
      this.onLeave = () => {
        this.paused = false;
      };
    }

    connectedCallback() {
      this.track = this.querySelector('.onyu-ticker__track');
      this.group = this.querySelector('.onyu-ticker__group');
      if (!this.track || !this.group) return;

      this.mode = this.dataset.mode === 'ticker' ? 'ticker' : 'fade';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', this.onMotionChange);
      window.addEventListener('resize', this.onResize);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);

      this.layout();
      // Satoshi loads after first paint and changes the measured width.
      if (this.mode === 'ticker' && document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.layout());
      }
    }

    disconnectedCallback() {
      this.stopFade();
      window.removeEventListener('resize', this.onResize);
      if (this.reducedMotion) this.reducedMotion.removeEventListener('change', this.onMotionChange);
    }

    layout() {
      if (!this.track || !this.group || !this.isConnected) return;
      if (this.mode === 'ticker') {
        this.layoutTicker();
      } else {
        this.layoutFade();
      }
    }

    /* ---------- Fade: one announcement at a time ---------- */

    layoutFade() {
      this.stopFade();
      this.cells = Array.from(this.group.querySelectorAll('.onyu-ticker__cell'));
      if (!this.cells.length) return;

      this.index = 0;
      this.cells.forEach((cell, i) => this.setCellState(cell, i === 0));
      this.classList.add('is-ready');
      if (this.cells.length < 2) return;

      const interval = (parseFloat(this.dataset.interval) || 4) * 1000;
      this.timer = setInterval(() => {
        if (!this.paused) this.advance();
      }, Math.max(2000, interval));
    }

    stopFade() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      const current = this.cells[this.index];
      this.index = (this.index + 1) % this.cells.length;
      this.show(this.cells[this.index], current);
    }

    show(next, current) {
      if (current && current !== next) {
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        this.setCellState(current, false);
        setTimeout(() => current.classList.remove('is-leaving'), this.fadeDuration);
      }
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      this.setCellState(next, true);
    }

    // Hidden announcements stay in the page for screen readers, but must not be a Tab stop.
    setCellState(cell, active) {
      cell.classList.toggle('is-active', active);
      cell.querySelectorAll('a').forEach((link) => {
        if (active) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    }

    /* ---------- Ticker: one continuously scrolling line ---------- */

    layoutTicker() {
      // Back to the single rendered group, so a resize re-measures from a known state.
      this.classList.remove('is-ready');
      this.style.removeProperty('--onyu-ticker-shift');
      this.style.removeProperty('--onyu-ticker-duration');
      while (this.track.children.length > 1) this.track.lastElementChild.remove();

      if (this.reducedMotion.matches) return;

      const groupWidth = this.group.getBoundingClientRect().width;
      const barWidth = this.getBoundingClientRect().width;
      if (groupWidth < 1 || barWidth < 1) return; // hidden or not laid out yet

      const copies = Math.max(1, Math.ceil(barWidth / groupWidth));
      const shift = groupWidth * copies;
      const speed = parseFloat(this.dataset.speed) || 55;

      const fragment = document.createDocumentFragment();
      for (let i = 1; i < copies * 2; i++) fragment.appendChild(this.cloneGroup());
      this.track.appendChild(fragment);

      this.style.setProperty('--onyu-ticker-shift', `${shift}px`);
      this.style.setProperty('--onyu-ticker-duration', `${Math.max(this.minDuration, shift / speed)}s`);
      this.classList.add('is-ready');
    }

    cloneGroup() {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      // Theme editor ids must stay unique to the original group.
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((cell) => {
        cell.removeAttribute('data-shopify-editor-block');
      });
      return clone;
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('onyu-ticker', OnyuTicker);

  // Theme editor: hold still on the announcement being edited.
  document.addEventListener('shopify:block:select', (event) => {
    const cell = event.target.closest && event.target.closest('.onyu-ticker__cell');
    const ticker = event.target.closest && event.target.closest('onyu-ticker');
    if (!ticker) return;
    ticker.paused = true;
    ticker.classList.add('is-paused');
    if (cell && ticker.mode === 'fade' && ticker.cells) {
      const index = ticker.cells.indexOf(cell);
      if (index > -1) {
        ticker.show(cell, ticker.cells[ticker.index]);
        ticker.index = index;
      }
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const ticker = event.target.closest && event.target.closest('onyu-ticker');
    if (!ticker) return;
    ticker.paused = false;
    ticker.classList.remove('is-paused');
  });
}
